import { useState, useEffect, useContext } from "react";

// react-router components
import { useLocation } from "react-router-dom";

// @material-ui core components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Icon from "@mui/material/Icon";

// Material Dashboard 2 PRO React TS components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

// Custom styles for DashboardNavbar
import { navbar, navbarContainer, navbarRow, navbarDesktopMenu } from "examples/Navbars/DashboardNavbar/styles";

// Material Dashboard 2 PRO React context
import { useMaterialUIController, setTransparentNavbar, setMiniSidenav } from "context";
import colors from "assets/theme-dark/base/colors";
import { context } from "utils/Provider";
import { CRUXY_ADMIN, CRUXY_USER } from "common/names";
import { CubesResponse, useCubesLazyQuery } from "graphql/types/graphql";

// Declaring prop types for DashboardNavbar
interface Props {
  absolute?: boolean;
  light?: boolean;
  isMini?: boolean;
}

function DashboardNavbar({ absolute, light, isMini }: Props): JSX.Element {
  const { user, selectedCube, setSelectedCube } = useContext(context);

  const [getCubes, { data: fetchedCubes }] = useCubesLazyQuery({
    notifyOnNetworkStatusChange: true,
  });

  const [cubes, setCubes] = useState<CubesResponse[]>([]);
  const [navbarType, setNavbarType] = useState<"fixed" | "absolute" | "relative" | "static" | "sticky">();
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, darkMode } = controller;
  const [menu, setMenu] = useState(null);

  const openMenu = (event: any) => setMenu(event.currentTarget);
  const closeMenu = () => setMenu(null);

  const { background } = colors;

  useEffect(() => {
    if (user) {
      getCubes({
        variables: {
          filter: {
            cubeId: null,
          },
        },
      });
    }
  }, [user, getCubes]);

  useEffect(() => {
    if (fetchedCubes?.cubes) {
      setCubes(fetchedCubes.cubes);
    }
  }, [fetchedCubes]);

  useEffect(() => {
    // Setting the navbar type
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    // A function that sets the transparent state of the navbar.
    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }

    /** 
     The event listener that's calling the handleTransparentNavbar function when 
     scrolling the window.
    */
    window.addEventListener("scroll", handleTransparentNavbar);

    // Call the handleTransparentNavbar function to set the state with the initial value.
    handleTransparentNavbar();

    // Remove event listener on cleanup
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);

  // Render the notifications menu

  // Styles for the navbar icons
  const iconsStyle = ({ palette: { dark, white, text }, functions: { rgba } }: { palette: any; functions: any }) => ({
    color: () => {
      let colorValue = light || darkMode ? text.main : dark.main;

      if (transparentNavbar && !light) {
        colorValue = darkMode ? rgba(text.main, 0.6) : text.main;
      }

      return colorValue;
    },
  });

  const renderMenu = (
    <Menu
      anchorEl={menu}
      anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      transformOrigin={{ vertical: "top", horizontal: "center" }}
      open={Boolean(menu)}
      onClose={closeMenu}
      keepMounted
    >
      {cubes?.map((cube: CubesResponse) => (
        <MenuItem
          key={cube?.name}
          onClick={() => {
            closeMenu();
            setSelectedCube({ cubeId: cube.cubeId, name: cube.name });
          }}
        >
          {cube?.name}
        </MenuItem>
      ))}
    </Menu>
  );

  return (
    <AppBar
      data-testid="appBarId"
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      // sx={(theme) => navbar(theme, { transparentNavbar, absolute, light, darkMode })}
      sx={(theme) => ({
        ...navbar(theme, { transparentNavbar, absolute, light, darkMode }),
        top: 0,
        marginTop: 0,
      })}
    >
      <Toolbar sx={{ ...navbarContainer }}>
        <MDBox color="inherit" mb={{ xs: 1, md: 0 }} sx={(theme) => navbarRow(theme, { isMini })}>
          <IconButton sx={navbarDesktopMenu} onClick={handleMiniSidenav} size="small" disableRipple>
            <Icon fontSize="medium" sx={{ iconsStyle }}>
              {miniSidenav ? "menu_open" : "menu"}
            </Icon>
          </IconButton>
        </MDBox>

        {(user?.role === CRUXY_ADMIN || user?.role === CRUXY_USER) && (
          <MDBox sx={(theme) => navbarRow(theme, { isMini })}>
            <MDBox display="flex">
              <MDButton
                size="small"
                variant={menu ? "contained" : "contained"}
                sx={{
                  width: "9rem",
                  backgroundColor: "#0D00FF",
                  borderRadius: "8px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: "0 8px",
                  "& .cube-name": {
                    maxWidth: "7rem",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  },
                }}
                onClick={openMenu}
                disableElevation
              >
                <span className="cube-name">{selectedCube?.name || "Select Cube"}</span>
                <Icon>keyboard_arrow_down</Icon>
              </MDButton>
              {renderMenu}
            </MDBox>
          </MDBox>
        )}
      </Toolbar>
    </AppBar>
  );
}

// Declaring default props for DashboardNavbar
DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

export default DashboardNavbar;
